import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv, txr } from "../lib/tx";

/**
 * "Read while you wait" summary for the quishing-restaurant start screen. Three
 * short slides (what/who + why a human is the control, what to watch for, key
 * terms) shown in the reader while the 3D room downloads. Content only; the
 * reader UI lives in ui/StartReader.tsx and the shared toolkit in
 * ui/readingArt.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. a table QR card: a code you cannot read, with a "where to?" tag  */
/* ------------------------------------------------------------------ */
const QR_ROWS = [
  "11101010111",
  "10101101101",
  "11100110111",
  "00001011000",
  "11010100110",
  "01101011010",
  "10110010101",
  "00001101100",
  "11101001011",
  "10101110010",
  "11100101101",
];

function TableQrArt(): JSX.Element {
  const H = 296;
  const cell = 9;
  const qx = 130.5;
  const qy = 90;
  return (
    <Frame label={tx("Example: code on a restaurant table")} aria={tx("A table card with a Scan for Menu QR code and a note that you cannot see where it leads")} height={H}>
      <rect x="60.5" y="38.5" width="239" height="196" rx="12" fill={C.panel2} stroke={C.line} />
      <AppChip x={72} y={48} />
      <text x="104" y="65" fontSize="13.5" fontWeight="700" fill={C.text}>
        {tx("Ristorante")}
      </text>
      <text x="266" y="65" fontSize="10.5" fill={C.muted} textAnchor="end">
        {tx("Table 12")}
      </text>
      <line x1="72" y1="82.5" x2="288" y2="82.5" stroke={C.line} />

      <rect x={qx - 8} y={qy - 6} width={cell * 11 + 16} height={cell * 11 + 12} rx="6" fill={C.text} />
      {QR_ROWS.map((row, r) =>
        row.split("").map((v, c) =>
          v === "1" ? <rect key={`${r}-${c}`} x={qx + c * cell} y={qy + r * cell} width={cell - 1} height={cell - 1} rx="1" fill={C.code} /> : null,
        ),
      )}
      <rect
        className="sr-anim-ring"
        x={qx - 8}
        y={qy - 6}
        width={cell * 11 + 16}
        height={cell * 11 + 12}
        rx="6"
        fill="none"
        stroke={C.amber}
        strokeWidth="1.6"
      />
      <text x="180" y="222" fontSize="12.5" fontWeight="700" fill={C.text} textAnchor="middle">
        {tx("Scan for Menu")}
      </text>

      <rect x="16.5" y="248.5" width="327" height="32" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-pulse" cx="33" cy="264.5" r="5.5" fill={C.amber} fillOpacity="0.25" stroke={C.amber} strokeWidth="1.4" />
      <text x="46" y="268.5" fontSize="12" fontWeight="600" fill={C.amber}>
        {txr("You can’t see where it leads", {})}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. look-alike address + over-asking permission prompts              */
/* ------------------------------------------------------------------ */
type PermGlyph = "camera" | "mic" | "pin";

function PermIcon({ g }: { g: PermGlyph }): JSX.Element {
  const s = { fill: "none", stroke: C.amber, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  if (g === "camera") {
    return (
      <g {...s}>
        <rect x="-8" y="-5.5" width="16" height="11" rx="2.5" />
        <circle cx="0" cy="0" r="2.8" />
      </g>
    );
  }
  if (g === "mic") {
    return (
      <g {...s}>
        <rect x="-3" y="-8" width="6" height="10" rx="3" />
        <path d="M-6 -1 a6 6 0 0 0 12 0" />
        <line x1="0" y1="5" x2="0" y2="8" />
      </g>
    );
  }
  return (
    <g {...s}>
      <path d="M0 8 C-6 1 -6 -1 -6 -3 a6 6 0 0 1 12 0 c0 2 0 4 -6 11 z" />
      <circle cx="0" cy="-3" r="2" />
    </g>
  );
}

function PermRow({ y, g, title, sub }: { y: number; g: PermGlyph; title: string; sub: string }): JSX.Element {
  return (
    <g>
      <rect x="16.5" y={y + 0.5} width="327" height="43" rx="8" fill={C.panel2} stroke={C.line} />
      <circle cx="40" cy={y + 22} r="15" fill={C.amber} fillOpacity="0.12" />
      <g transform={`translate(40 ${y + 22})`}>
        <PermIcon g={g} />
      </g>
      <text x="64" y={y + 19} fontSize="12.5" fontWeight="700" fill={C.text}>
        {txv(title)}
      </text>
      <text x="64" y={y + 34} fontSize="10.5" fill={C.muted}>
        {txv(sub)}
      </text>
      <rect x="258" y={y + 11} width="38" height="22" rx="11" fill="none" stroke={C.line} />
      <text x="277" y={y + 26} fontSize="10.5" fill={C.muted} textAnchor="middle">
        {tx("Allow")}
      </text>
      <rect x="301" y={y + 11} width="36" height="22" rx="11" fill={C.mint} fillOpacity="0.16" stroke={C.mint} strokeOpacity="0.5" />
      <text x="319" y={y + 26} fontSize="10.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Deny")}
      </text>
    </g>
  );
}

function LookAlikeArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label={tx("Example: page opened from the code")} aria={tx("A web address with a number one in place of the letter l, and three permission requests that a menu does not need")} height={H}>
      <rect x="16.5" y="36.5" width="327" height="30" rx="15" fill={C.code} stroke={C.line} />
      <circle cx="33" cy="51.5" r="4.5" fill="none" stroke={C.muted} strokeWidth="1.3" />
      <text x="46" y="55.5" fontSize="12" fontFamily={MONO} fill={C.soft}>
        {txr("restaurant-lu{tspan}gi.com", { tspan: <tspan fill={C.red} fontWeight="700">1</tspan> })}</text>
      <rect className="sr-anim-pulse" x="258" y="40" width="80" height="23" rx="11.5" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.5" style={{ transformOrigin: "298px 51.5px" }} />
      <text x="298" y="55.2" fontSize="10" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.5">
        {tx("LOOK-ALIKE")}
      </text>
      <text x="20" y="84" fontSize="10.5" fill={C.muted}>
        {tx("The real address uses a letter l, not a number 1.")}
      </text>

      <PermRow y={96} g="camera" title={tx("Use your camera?")} sub={tx("to “scan loyalty codes”")} />
      <PermRow y={146} g="mic" title={tx("Use your microphone?")} sub={tx("for “voice ordering”")} />
      <PermRow y={196} g="pin" title={tx("Know your location?")} sub={tx("to “confirm your table”")} />

      <rect x="16.5" y="252.5" width="327" height="32" rx="8" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="33" cy="268.5" r="5.5" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <circle cx="33" cy="268.5" r="1.8" fill={C.mint} />
      <text x="46" y="272.5" fontSize="12" fontWeight="600" fill={C.mint}>
        {tx("A menu needs none of these")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. an urgent video call with a verify step                          */
/* ------------------------------------------------------------------ */
function CallArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: incoming video call")} aria={tx("A video call from a relative asking for urgent money, with a step to hang up and call back on a number you know")} height={H}>
      <rect x="16.5" y="36.5" width="327" height="132" rx="10" fill={C.code} stroke={C.line} />
      <g className="sr-anim-float">
        <circle cx="180" cy="86" r="24" fill={C.panel2} stroke={C.line} />
        <path d="M128 168 C128 126 232 126 232 168 z" fill={C.panel2} stroke={C.line} />
      </g>
      <text x="30" y="56" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Giovanni")}
      </text>
      <circle className="sr-anim-blink" cx="302" cy="52" r="3.5" fill={C.red} />
      <text x="312" y="56" fontSize="10.5" fontWeight="700" fill={C.red}>
        {tx("LIVE")}
      </text>
      <rect x="28.5" y="132.5" width="303" height="26" rx="6" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <text x="180" y="149.5" fontSize="11.5" fontWeight="600" fill={C.amber} textAnchor="middle">
        {tx("“Mum, send EUR 3,200 right now.”")}
      </text>

      <rect x="16.5" y="180.5" width="327" height="44" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="186" width="3" height="33" rx="1.5" fill={C.red} />
      <text x="32" y="199" fontSize="12" fontWeight="700" fill={C.red}>
        {tx("Urgent, emotional, about money")}
      </text>
      <text x="32" y="215" fontSize="10.5" fill={C.muted}>
        {tx("The face and voice look right. The pressure is the warning.")}
      </text>

      <rect x="16.5" y="236.5" width="327" height="46" rx="8" fill={C.mint} fillOpacity="0.1" stroke={C.mint} strokeOpacity="0.4" />
      <circle className="sr-anim-pulse" cx="38" cy="259.5" r="10" fill={C.mint} fillOpacity="0.16" />
      <polyline points="33,259.5 36.5,263 43,255.5" fill="none" stroke={C.mint} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <text x="58" y="256" fontSize="12" fontWeight="700" fill={C.mint}>
        {tx("Hang up. Call back")}
      </text>
      <text x="58" y="272" fontSize="10.5" fill={C.soft}>
        {tx("on a number you already have.")}
      </text>
    </Frame>
  );
}

export const QUISHING_RESTAURANT_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "A menu code is a link you can\u2019t read.",
    illustration: TableQrArt,
    caption: "Check where it leads before you open it.",
    sections: [
      {
        h: "What this is about",
        body: "Lunch on a sunny terrace. You scan the menu code on the table, and a scam unfolds that ends in a call asking for money. You make each call.",
      },
      {
        h: "Who it\u2019s for",
        body: "Anyone with a phone. No technical knowledge needed.",
      },
      {
        h: "Why your judgement matters",
        body: "A QR code hides its address, so no filter reads it for you. Only you can check where it leads, refuse what it asks for, and verify a call before money moves.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "Three moments to stop and check.",
    illustration: LookAlikeArt,
    caption: "Deny what the page doesn\u2019t need.",
    sections: [
      {
        h: "Read the address first",
        body: "Look at the link before the page loads. A look-alike swaps one character, like a number 1 for a letter l.",
      },
      {
        h: "Refuse what it doesn\u2019t need",
        body: "A menu needs no camera, microphone or location. Tap Deny, or close the page.",
      },
      {
        h: "Allowed access gets used",
        body: "Photos, your voice and your location are enough to build a convincing fake of you or someone you love.",
      },
      {
        h: "Pressure is the signal",
        body: "A call that is urgent, emotional and about money fits the scam pattern, even when the face and voice seem right.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: CallArt,
    caption: "A familiar face is not proof.",
    sections: [
      { h: "Quishing", body: "Phishing through a QR code. The code hides the address, so you can\u2019t judge the link by looking at it." },
      { h: "Look-alike domain", body: "A web address built to resemble a real one, with a swapped or extra character." },
      { h: "Permission prompt", body: "The phone asking to use your camera, microphone or location. You choose; Deny is always allowed." },
      { h: "Deepfake", body: "A fake video or voice made from real photos and recordings of a person." },
      { h: "Urgency", body: "Pressure to act before you can think or check. It is the scammer\u2019s main tool." },
      { h: "Call-back check", body: "Hang up and reach the person on a number you already know. If it was a fake, the story ends there." },
    ],
  },
];
